import { WarnIcon } from "@assets";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { Flex, Text, useColorModeValue } from "@chakra-ui/react";
import Dialog, { DialogProps } from "@components/Dialog/Dialog";
import { UIFormField } from "@components/DynamicForm/types";
import LoadingAnimation from "@components/Loader/LoadingAnimation";
import { FormFieldType } from "@models/form";
import {
  DEFAULT_TRANSACTION_CONTACT_FIELD_KEYS,
  PaymentMethod,
  Transaction,
  TransactionParam,
} from "@models/payment";
import { normalizeId } from "@models/utils";
import { useUserStore } from "@providers/auth";
import { PaymentStep, usePaymentStore } from "@providers/checkout/payment";
import * as Sentry from "@sentry/browser";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import CheckoutAcknowledgement from "./CheckoutAcknowledgement";
import CheckoutBillingForm from "./CheckoutBillingForm";
import CheckoutContactForm from "./CheckoutContactForm";
import CheckoutPaymentMethod from "./CheckoutPaymentMethod";
import CheckoutReferralCode from "./CheckoutReferralCode";

const _t = (key?: string) => key || "";

const BILLING_FORM_FIELDS: UIFormField[] = [
  {
    type: FormFieldType.TEXT,
    label: {
      en: "Name",
    },
    key: "name",
    required: true,
  },
  {
    type: FormFieldType.EMAIL,
    label: {
      en: "Email Address",
    },
    key: "email",
    required: true,
    span: 1,
  },
];

const DEFAULT_PAYMENT_METHODS = [PaymentMethod.STRIPE, PaymentMethod.COINGATE];

const CheckoutOrderDetail = ({
  setAllowSubmit,
}: {
  setAllowSubmit: (allow: boolean) => void;
}) => {
  const user = useUserStore((state) => state.user);
  const transaction = usePaymentStore((state) => state.transaction);
  const event = usePaymentStore((state) => state.event);
  const [contactFormValid, setContactFormValid] = useState(false);
  const [billingFormValid, setBillingFormValid] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  // useBeforeunload((e) => {
  //   console.log("beforeunload");
  //   e.preventDefault();
  //   return _t("Are you sure you want to leave? Your input would be lost.");
  // });

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: "checkout:payment",
      level: "info",
    });
  }, []);

  useEffect(() => {
    if (!transaction) return;
    console.log(
      "checkout valid",
      contactFormValid,
      billingFormValid,
      acknowledged
    );
    setAllowSubmit(
      contactFormValid &&
        acknowledged &&
        (transaction.finalAmount > 0 ? billingFormValid : true)
    );
  }, [contactFormValid, billingFormValid, acknowledged, transaction]);

  if (!event || !transaction || !user)
    return (
      <div className="flex flex-row flex-grow w-full h-full justify-center items-center">
        <LoadingAnimation />
      </div>
    );

  const handleBillingFormChange = (values: FieldValues) => {
    const _transaction = usePaymentStore.getState().transaction;
    if (!_transaction) return;
    for (const field of BILLING_FORM_FIELDS) {
      _transaction.contact[field.key] = values[field.key];
      Sentry.setTag(`transaction.contact.${field.key}`, values[field.key]);
    }
    usePaymentStore.setState({
      transaction: new Transaction(_transaction.json as TransactionParam),
    });
  };

  const handleContactFormChange = (idx: number, values: FieldValues) => {
    const _transaction = usePaymentStore.getState().transaction;
    if (!_transaction) return;
    console.log("handleContactFormChange", _transaction, values);
    const extraInfo: { key: string; value: string }[] = [];
    for (const key of Object.keys(values)) {
      if (key.startsWith("_")) continue;
      if (key === "bulkQuantity") {
        _transaction.items[idx].quantity = Number(values[key]);
      } else if (DEFAULT_TRANSACTION_CONTACT_FIELD_KEYS.includes(key)) {
        _transaction.items[idx].contact[key] = values[key];
      } else if (values[key]) {
        extraInfo.push({ key, value: values[key] });
      }
    }
    if (!isEmpty(extraInfo)) _transaction.items[idx].extraInfo = extraInfo;
    usePaymentStore.setState({
      transaction: new Transaction(_transaction.json as TransactionParam),
    });
  };

  const handleContactFormValidChange = (idx: number, valid: boolean) => {
    const _transaction = usePaymentStore.getState().transaction;
    if (!_transaction) return;
    console.log("handleContactFormValidChange", _transaction);
    _transaction.items[idx].valid = !!valid;
    setContactFormValid(_transaction.items.every((item) => item.valid));
    usePaymentStore.setState({
      transaction: new Transaction(_transaction.json as TransactionParam),
    });
  };

  const handleBillingFormValidChange = (valid: boolean) => {
    setBillingFormValid(!!valid);
  };

  const handleAcknowledgementChange = (agree: boolean) => {
    setAcknowledged(!!agree);
  };

  const handleReferralCodeChange = (referralCode: string) => {
    if (!referralCode) return;
    console.log(referralCode);
    const _transaction = usePaymentStore.getState().transaction;
    if (!_transaction) return;
    _transaction.referralCode = normalizeId(referralCode);
    usePaymentStore.setState({
      transaction: new Transaction(_transaction.json as TransactionParam),
    });
  };

  const handlePaymentMethodChange = (method: PaymentMethod) => {
    Sentry.setTag("transaction.paymentMethod", method);
    console.log(method);
    const _transaction = usePaymentStore.getState().transaction;
    if (!_transaction) return;
    _transaction.paymentMethod = method;
    usePaymentStore.setState({
      transaction: new Transaction(_transaction.json as TransactionParam),
    });
  };

  return (
    <div className="flex-grow flex flex-col w-full bg-transparent max-w-[60rem]">
      <div className="flex flex-row items-baseline" />
      <div className="flex flex-col gap-6 sm:gap-8">
        <CheckoutContactForm
          onChange={handleContactFormChange}
          onValidChange={handleContactFormValidChange}
        />

        {transaction.finalAmount > 0 && (
          <>
            <CheckoutBillingForm
              fields={BILLING_FORM_FIELDS}
              onChange={handleBillingFormChange}
              onValidChange={handleBillingFormValidChange}
            />
            <CheckoutPaymentMethod
              paymentMethods={event.paymentMethods || DEFAULT_PAYMENT_METHODS}
              onChange={handlePaymentMethodChange}
            />
          </>
        )}
        <CheckoutReferralCode onChange={handleReferralCodeChange} />
        <CheckoutAcknowledgement onChange={handleAcknowledgementChange} />
      </div>
    </div>
  );
};

export const CheckoutOrderDetailTitle = () => {
  const [dialog, setDialog] = useState<DialogProps | null>(null);

  const confirmBackDialog: DialogProps = {
    onClose: () => setDialog(null),
    icon: WarnIcon,
    title: _t("Leave Page"),
    actions: [
      {
        text: _t("cancel"),
        onClick: () => setDialog(null),
      },
      {
        text: _t("confirm"),
        primary: true,
        onClick: () => {
          Sentry.addBreadcrumb({
            category: "checkout:backToSelect",
            level: "info",
          });
          usePaymentStore.setState({ currentStep: PaymentStep.SELECT_ITEMS });
          setDialog(null);
          window.scrollTo(0, 0);
        },
      },
    ],
    children: _t(
      "Are you sure you want to leave this page? Your changes will be lost."
    ),
  };

  return (
    <>
      <Flex direction="row" alignItems="center" gap={4} px={[8, 0]}>
        <ChevronLeftIcon
          onClick={() => setDialog(confirmBackDialog)}
          cursor="pointer"
          boxSize="2rem"
          color={useColorModeValue("", "white")}
        />
        <Text fontSize="2xl" textColor={useColorModeValue("", "white")}>
          {_t("Checkout")}
        </Text>
      </Flex>

      {dialog && (
        <Dialog
          isOpen={true}
          onClose={dialog.onClose}
          title={dialog.title}
          actions={dialog.actions}
          loading={dialog.loading}
          icon={dialog.icon}
          onOpen={dialog.onOpen}
        >
          <p className="w-full text-center">{dialog.children}</p>
        </Dialog>
      )}
    </>
  );
};

export default CheckoutOrderDetail;
